// Settings: a small popover under the title bar's button. Sound and its volume, the
// performance readout, the 3D look, and the developer options.
import { getVolume, isMuted, setMuted, setVolume } from './sound.ts';
import { perfEnabled, setPerfEnabled } from './perf.ts';

const DEV_KEY = 'thrash.dev';
const LOOK_KEY = 'thrash.3d';

/** Whether the developer options (Wave test, Stress test, Reset to sample) are shown. */
export function devEnabled(): boolean {
  if (import.meta.env.DEV) return true;
  try { return new URLSearchParams(location.search).has('dev') || localStorage.getItem(DEV_KEY) === '1'; } catch { return false; }
}

function is3DSaved(): boolean {
  try { return localStorage.getItem(LOOK_KEY) !== '0'; } catch { return true; }
}

export function openSettings(anchor: HTMLElement, cb: { onPerf(): void; onDev(): void }): void {
  const old = document.getElementById('settings');
  if (old) { old.remove(); return; }
  const box = document.createElement('div');
  box.id = 'settings';
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-label', 'Settings');
  const row = (label: string, input: HTMLElement, note = ''): HTMLLabelElement => {
    const l = document.createElement('label');
    l.append(input, ` ${label}`);
    if (note) { const n = document.createElement('span'); n.className = 'muted'; n.textContent = ` ${note}`; l.appendChild(n); }
    box.appendChild(l);
    return l;
  };
  const check = (on: boolean, fn: (v: boolean) => void): HTMLInputElement => {
    const i = document.createElement('input');
    i.type = 'checkbox';
    i.checked = on;
    i.onchange = () => fn(i.checked);
    return i;
  };
  const sound = check(!isMuted(), v => { setMuted(!v); volume.disabled = !v; });
  row('Sound', sound);
  const volume = document.createElement('input');
  volume.type = 'range';
  volume.min = '0'; volume.max = '1'; volume.step = '0.05';
  volume.value = String(getVolume());
  volume.disabled = isMuted();
  volume.setAttribute('aria-label', 'Volume');
  volume.oninput = () => setVolume(Number(volume.value));
  row('Volume', volume);
  row('Performance readout', check(perfEnabled(), v => { setPerfEnabled(v); cb.onPerf(); }), 'Shift+F3');
  row('3D look', check(is3DSaved(), v => { try { localStorage.setItem(LOOK_KEY, v ? '1' : '0'); } catch { /* ignore */ } location.reload(); }), 'reloads the page');
  row('Developer options', check(devEnabled(), v => { try { localStorage.setItem(DEV_KEY, v ? '1' : '0'); } catch { /* ignore */ } cb.onDev(); }), 'test modes under More');
  const r = anchor.getBoundingClientRect();
  box.style.top = `${Math.round(r.bottom + 6)}px`;
  box.style.right = `${Math.max(8, Math.round(window.innerWidth - r.right))}px`;
  document.body.appendChild(box);
  // a click anywhere else, or Escape, shuts it
  const away = (e: MouseEvent) => { if (!box.contains(e.target as Node) && e.target !== anchor) { shut(); } };
  const key = (e: KeyboardEvent) => { if (e.key === 'Escape') shut(); };
  const shut = () => { box.remove(); document.removeEventListener('mousedown', away); document.removeEventListener('keydown', key); };
  setTimeout(() => { document.addEventListener('mousedown', away); document.addEventListener('keydown', key); }, 0);
}
